import { useCallback, useEffect, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Tv, ShoppingCart, DollarSign, ExternalLink } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { InlineError } from '@/components/ui/inline-error';


interface Provider {
  provider_id: number;
  provider_name: string;
  logo_path: string;
  display_priority: number;
}

interface WatchProvidersProps {
  tmdbId: number;
  mediaType: 'movie' | 'tv';
  userId?: string;
}

export const WatchProviders = ({ tmdbId, mediaType, userId }: WatchProvidersProps) => {
  const [providers, setProviders] = useState<{
    flatrate: Provider[];
    rent: Provider[];
    buy: Provider[];
    link: string;
  } | null>(null);
  const [loading, setLoading] = useState(true);
  const [failed, setFailed] = useState(false);

  const fetchProviders = useCallback(async () => {
    // Require authentication
    if (!userId) {
      setLoading(false);
      return;
    }

    setLoading(true);
    setFailed(false);
    try {
      const { data, error } = await supabase.functions.invoke('tmdb-providers', {
        body: { tmdbId, mediaType }
      });

      if (error) {
        // Handle auth errors gracefully
        if (error.message?.includes('Unauthorized') || error.message?.includes('401')) {
          console.log('Auth required for watch providers');
          return;
        }
        throw error;
      }
      setProviders(data);
    } catch (error) {
      console.error('Error fetching providers:', error);
      setFailed(true);
    } finally {
      setLoading(false);
    }
  }, [tmdbId, mediaType, userId]);

  useEffect(() => {
    fetchProviders();
  }, [fetchProviders]);


  // Don't render if not authenticated
  if (!userId) {
    return null;
  }

  if (loading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Tv className="h-5 w-5" />
            Where to Watch
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="animate-pulse space-y-3">
            <div className="h-20 bg-muted rounded"></div>
          </div>
        </CardContent>
      </Card>
    );
  }

  if (failed) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Tv className="h-5 w-5" />
            Where to Watch
          </CardTitle>
        </CardHeader>
        <CardContent>
          <InlineError
            message="We couldn't reach the streaming availability service."
            onRetry={fetchProviders}
          />
        </CardContent>
      </Card>
    );
  }

  if (!providers || (!providers.flatrate?.length && !providers.rent?.length && !providers.buy?.length)) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Tv className="h-5 w-5" />
            Where to Watch
          </CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-sm text-muted-foreground">
            Streaming availability not found for your region.
          </p>
        </CardContent>
      </Card>
    );
  }


  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Tv className="h-5 w-5" />
          Where to Watch
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Streaming Services */}
        {providers.flatrate && providers.flatrate.length > 0 && (
          <div className="space-y-2">
            <div className="flex items-center gap-2 text-sm font-medium">
              <Tv className="h-4 w-4" />
              Stream
            </div>
            <div className="flex flex-wrap gap-2">
              {providers.flatrate.map((provider) => (
                <div
                  key={provider.provider_id}
                  className="flex items-center gap-2 bg-muted rounded-lg p-2"
                  title={provider.provider_name}
                >
                  <img
                    src={`https://image.tmdb.org/t/p/w92${provider.logo_path}`}
                    alt={provider.provider_name}
                    className="h-8 w-8 rounded"
                  />
                  <span className="text-xs font-medium">{provider.provider_name}</span>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Rent Options */}
        {providers.rent && providers.rent.length > 0 && (
          <div className="space-y-2">
            <div className="flex items-center gap-2 text-sm font-medium">
              <DollarSign className="h-4 w-4" />
              Rent
            </div>
            <div className="flex flex-wrap gap-2">
              {providers.rent.slice(0, 4).map((provider) => (
                <div
                  key={provider.provider_id}
                  className="flex items-center gap-2 bg-muted rounded-lg p-2"
                  title={provider.provider_name}
                >
                  <img
                    src={`https://image.tmdb.org/t/p/w92${provider.logo_path}`}
                    alt={provider.provider_name}
                    className="h-8 w-8 rounded"
                  />
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Buy Options */}
        {providers.buy && providers.buy.length > 0 && (
          <div className="space-y-2">
            <div className="flex items-center gap-2 text-sm font-medium">
              <ShoppingCart className="h-4 w-4" />
              Buy
            </div>
            <div className="flex flex-wrap gap-2">
              {providers.buy.slice(0, 4).map((provider) => (
                <div
                  key={provider.provider_id}
                  className="flex items-center gap-2 bg-muted rounded-lg p-2"
                  title={provider.provider_name}
                >
                  <img
                    src={`https://image.tmdb.org/t/p/w92${provider.logo_path}`}
                    alt={provider.provider_name}
                    className="h-8 w-8 rounded"
                  />
                </div>
              ))}
            </div>
          </div>
        )}

        {/* JustWatch Link */}
        {providers.link && (
          <Button variant="outline" size="sm" className="w-full" asChild>
            <a href={providers.link} target="_blank" rel="noopener noreferrer">
              <ExternalLink className="h-4 w-4 mr-2" />
              View All Options on JustWatch
            </a>
          </Button>
        )}

        <p className="text-xs text-muted-foreground">
          Availability may vary by region. Data provided by JustWatch.
        </p>
      </CardContent>
    </Card>
  );
};
